import { Edge, useGraphInstance, Node } from "@antv/xflow"
import { memo, useCallback, useEffect, useState } from "react"
import { createCanvasEvent$, eventToObserverable,  } from "../../events/helper"
import { filter, merge, Observable, switchMap, takeUntil, tap, throttleTime } from "rxjs"
import { createBaseStream } from "../../nodes"
import { EventMap, useEditorEventBusInstance } from "../../events/useEventBusInstance"
import { createNozzleToNode, createNozzleToNodeAndConnect } from "../../utils"
import { v4 as uuidv4 } from "uuid";

interface DrawingStreamProps {
}
export const DrawingStream = memo((props: DrawingStreamProps) => {
  const graph = useGraphInstance()
  const eventBus = useEditorEventBusInstance()
  const [key, setKey] = useState(uuidv4());
  let line: Edge | null = null


  const reset = useCallback(() => {
    line = null
    setKey(uuidv4())
  }, []);
  
  useEffect(() => {
    if (!graph) return;
    const points: PositionLike[] = []
    const mouseDown$ = createCanvasEvent$(graph, 'mousedown')
    const mouseMove$ = createCanvasEvent$(graph, 'mousemove').pipe(throttleTime(20))
    const dblclick$ = createCanvasEvent$(graph, 'dblclick')
    let portClickEvt: any = null
    const portClick$ = eventToObserverable(graph, 'node:port:click').pipe(
      filter((evt: any) => !!line),
      tap((evt: any) => {
        portClickEvt = evt
      })
    )


    let lastEdgeClick: any = null
    const edgeClick$ = eventBus.on$(EventMap.nozzleEdgeClick).pipe(
      tap((evt: any) => {
        lastEdgeClick = evt
      })
    )

    const end$ = merge(dblclick$, edgeClick$.pipe(filter(() => !!line)), portClick$)

    const drawStart$ = eventBus.on$(EventMap.nozzleEdgeMousedown).pipe(
      filter(() => !line),
      switchMap((evt: any) => {
        const {nozzleEdge, node, e} = evt
        const point = graph.clientToLocal(e.clientX, e.clientY)
        const createLine = createNozzleToNode(nozzleEdge, node, graph).then((nozzleId) => {
          const baseOptions = createBaseStream(point, {x: point.x + 1, y: point.y + 1})
          baseOptions.source = {cell: node.id, port: nozzleId} as any
          return graph.createEdge(baseOptions)
        })

        return new Observable<Edge>((observer) => {
          createLine.then((oline) => {
            observer.next(oline)
          })
        })
      }),
      tap((oline) => {
        line = oline;
        line.setAttrByPath('path/pointerEvents', 'none')
        const lineData = line.getData()
        line.setData({...lineData, drawing: true})
        graph!.addEdge(line)
        lastEdgeClick = null
      }),
    )

    const normalDrawingStart$ = mouseDown$.pipe(
      tap((evt: any) => {
        const pageX = evt.clientX;
        const pageY = evt.clientY;

        const point = graph.clientToLocal(pageX, pageY);
        
        points.push(point)
      }),
      takeUntil(end$)
    )

    const sub = merge(drawStart$, normalDrawingStart$)
      .pipe(
        switchMap(() => {
          return mouseMove$
        }),
        takeUntil(end$)
      )
      .subscribe({
        next: (evt: any) => {
          const pageX = evt.clientX;
          const pageY = evt.clientY;

          const movePoint = graph!.clientToLocal(pageX, pageY);
          if (!line) {
            line = graph!.createEdge(createBaseStream(points[0], movePoint))
            line.setAttrByPath('path/pointerEvents', 'none')
            const lineData = line.getData()
            line.setData({...lineData, drawing: true})
            graph!.addEdge(line)
          } else {
            const source = line.getSource() as any
            if (source.cell && source.port) {
              line!.setVertices(points)
              line!.setTarget(movePoint)
            } else {
              const [start, ...others] = points
              line!.setSource(start)
              line!.setTarget(movePoint)
              line!.setVertices(others)
            }
          }
        },
        complete: () => {

          const offset = lastEdgeClick ? -1 : -2
        
          if (line) {
            if (lastEdgeClick) {
              const node: Node = lastEdgeClick.node
              const {nozzleEdge} = lastEdgeClick

              createNozzleToNodeAndConnect(graph, nozzleEdge, node, eventBus, line, 'target')

            } else if (portClickEvt) {
              const {node, port} = portClickEvt
              line!.setTarget({cell: node.id, port})
            } else {
              const source = line?.getSource() as any
              const sourceIsNozzle = source.cell && source.port
              if (sourceIsNozzle) {
                line.setTarget(points[points.length + offset])
                line.setVertices(points.slice(0, offset))
              } else {
                line.setSource(points[0])
                line.setTarget(points[points.length + offset])
                line.setVertices(points.slice(1, offset))
              }
            }
            const lineData = line!.getData()
            delete lineData.drawing
            line.setData({...lineData})
            line.removeAttrByPath('path/pointerEvents')
          }
          reset()
        }
      })


    return () => {
      sub.unsubscribe()
      line = null
    }
  }, [graph, reset, key])
  return null
})

